import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isVerifiedProvider } from '../../util/verifiedProvider';
import { track } from '../../util/analytics';

import NamedLink from '../NamedLink/NamedLink';

import css from './VerifiedProviderBanner.module.css';

const STORAGE_KEY = 'verifiedProviderBannerDismissed';
const BANNER_ID = 'verified_provider';

/**
 * Ausblendungen liegen pro Nutzer-ID im localStorage.
 *
 * Jeder Zugriff ist eingepackt: im privaten Fenster, bei blockierten Site-Daten
 * und in manchen In-App-Browsern wirft schon das Lesen. Ein Banner darf die
 * Seite, auf der er sitzt, nicht mitreissen — im Zweifel wird er gezeigt.
 */
const readDismissals = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
};

const writeDismissals = next => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // Ohne Speicher bleibt die Ausblendung auf diese Sitzung beschraenkt.
  }
};

/**
 * VerifiedProviderBanner
 *
 * Stupser fuer angemeldete Anbieter ohne Verifizierung. Erscheint auf den eigenen
 * Flaechen (Inserate verwalten, Inserate-Assistent, eigene Inseratsseite) und
 * fuehrt direkt auf die Auszahlungseinstellungen.
 *
 * Er ist ausblendbar, und die Ausblendung haelt ueber Neuladen hinweg — ein
 * Hinweis, der bei jedem Seitenaufruf zurueckkommt, wird nicht gelesen, sondern
 * weggeklickt. Sobald der Anbieter verifiziert ist, wird die Ausblendung
 * VERWORFEN: entzieht Stripe die Freigabe spaeter wieder, muss er das erfahren
 * und nicht an einer Entscheidung von vor Monaten haengenbleiben.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object} props.currentUser angemeldeter Nutzer (darf null sein)
 * @param {boolean} props.stripeStatusSynced hat der Stripe-Abgleich schon geantwortet?
 * @param {string?} props.placement Flaeche fuer die Analytics-Ereignisse
 * @param {Function?} props.onCta ersetzt den Verweis auf die Auszahlungsseite durch
 *   eine Aktion — fuer Flaechen, die man nicht verlassen darf (Inserate-Assistent)
 * @returns {JSX.Element|null}
 */
const VerifiedProviderBanner = props => {
  const { className, rootClassName, currentUser, stripeStatusSynced, placement, onCta } = props;
  const intl = useIntl();

  const userId = currentUser?.id?.uuid;
  const verified = isVerifiedProvider(currentUser);

  // null = der Speicher wurde noch nicht gelesen. Bewusst KEIN Vorgabewert
  // false: waehrend des ersten Durchlaufs ist unbekannt, ob ausgeblendet wurde,
  // und ein kurz aufblitzender Banner waere schlimmer als einer, der eine
  // Wimper spaeter kommt. Der Server rendert ohnehin abgemeldet (siehe
  // src/index.js), hier entsteht also kein Hydrationsunterschied.
  const [dismissed, setDismissed] = useState(null);

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (verified) {
      const next = readDismissals();
      if (next[userId]) {
        delete next[userId];
        writeDismissals(next);
      }
      setDismissed(true);
      return;
    }
    setDismissed(readDismissals()[userId] === true);
  }, [userId, verified]);

  // `stripeStatusSynced` ist der Unterschied zwischen "geprueft und nicht
  // verifiziert" und "noch nicht geprueft". Ohne ihn las der Stupser das
  // Fehlen von profile.metadata.stripeVerified als "nicht verifiziert" — und
  // weil syncStripeStatus() fire-and-forget laeuft (user.duck.js), FEHLT das
  // Feld beim ersten Render grundsaetzlich. Vor dem Backfill hiess das: jeder
  // Anbieter, auch der seit Monaten verifizierte, bekam "Werde verifizierter
  // Anbieter" zu sehen, und banner_shown feuerte fuer jeden einzelnen — genau
  // die Quote, die dieses Ereignis messen sollte.
  //
  // Das Badge darf die Gegenrichtung NICHT uebernehmen: dort bleibt es bei
  // `=== true`, weil ein Vertrauenszeichen im Zweifel schweigen muss. Hier ist
  // die vorsichtige Richtung umgekehrt — im Zweifel nicht nerven.
  const visible = !!userId && stripeStatusSynced && !verified && dismissed === false;

  // Erst melden, wenn der Hinweis wirklich steht. Ein `banner_shown` beim Mount
  // haette auch fuer verifizierte Anbieter und fuer weggeklickte Hinweise
  // gefeuert — die Quote "Klick je Einblendung" waere damit wertlos.
  useEffect(() => {
    // Siehe VerifiedBadge: ohne `placement` wird nicht gemeldet, damit die
    // Styleguide-Beispiele auf der Produktionsroute /styleguide die
    // Einblendungsquote nicht aufblasen.
    if (visible && placement) {
      track.bannerShown({ banner_id: BANNER_ID, placement });
    }
  }, [visible, placement]);

  if (!visible) {
    return null;
  }

  const handleCta = () => {
    if (placement) {
      track.bannerCtaClick({ banner_id: BANNER_ID, placement });
    }
    if (onCta) {
      onCta();
    }
  };

  const handleDismiss = () => {
    writeDismissals({ ...readDismissals(), [userId]: true });
    setDismissed(true);
  };

  return (
    // Kein <h2>: der Stupser wird an allen drei Stellen VOR die <h1> der Seite
    // gehaengt (ManageListingsPage vor "Deine Eintraege", beide ListingPage-
    // Varianten vor den Inseratstitel). Als Ueberschrift ausgezeichnet waere die
    // Reihenfolge h2 -> h1, axe meldet heading-order, und die Ueberschriften-
    // navigation (H in NVDA/VoiceOver) landete auf einem wegklickbaren Hinweis
    // statt auf dem Seitentitel. Der Text bleibt fett; die Region traegt den
    // Namen ueber aria-label, damit sie trotzdem anspringbar bleibt.
    <section
      className={classNames(rootClassName || css.root, className)}
      aria-label={intl.formatMessage({ id: 'VerifiedProviderBanner.heading' })}
    >
      <div className={css.text}>
        <p className={css.heading}>
          <FormattedMessage id="VerifiedProviderBanner.heading" />
        </p>
        <p className={css.body}>
          <FormattedMessage id="VerifiedProviderBanner.body" />
        </p>
      </div>
      <div className={css.actions}>
        {/* Mit `onCta` ein Knopf statt eines Verweises: im Inserate-Assistenten
            wuerde ein Seitenwechsel ungespeicherte Eingaben verwerfen, und der
            Assistent haelt fuer genau diese Aufgabe ein eigenes Modal bereit. */}
        {onCta ? (
          <button type="button" className={css.cta} onClick={handleCta}>
            <FormattedMessage id="VerifiedProviderBanner.cta" />
          </button>
        ) : (
          <NamedLink className={css.cta} name="StripePayoutPage" onClick={handleCta}>
            <FormattedMessage id="VerifiedProviderBanner.cta" />
          </NamedLink>
        )}
        <button
          type="button"
          className={css.dismiss}
          onClick={handleDismiss}
          aria-label={intl.formatMessage({ id: 'VerifiedProviderBanner.dismiss' })}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            aria-hidden="true"
            focusable="false"
            className={css.dismissIcon}
          >
            <path
              d="M1 1l12 12M13 1L1 13"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default VerifiedProviderBanner;
